/**
 * Audit: one big button per kind of check — Quality, Security, Deploy. Pick the repository once,
 * press a button, and the agent in it runs that audit until a checker finds nothing left: writing
 * the missing tests, repairing what is safe to repair, filing the rest as bugs.
 *
 * Meant to be usable by anybody. Each button says in one line what it will do; the prompt behind
 * it comes from the catalog and can be read or reworded on the Prompts screen.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, FlaskConical, FolderGit2, Gauge, Layers, Loader2, Rocket, ShieldCheck, TrendingUp, type LucideIcon } from 'lucide-react'
import { ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { useActiveRepository } from '../lib/activeRepository'
import { localized, runPrompt, usePromptCatalog } from '../lib/prompts'
import { EmptyState, ErrorBanner, Skeleton } from '../components/primitives'
import type { CatalogPrompt } from '../lib/types'
import { PluginButtons } from '../components/PluginButtons'
import { useWhoDoesIt } from '../components/WhoDoesIt'
import type { WorkTarget } from '../lib/prompts'

const ICONS: Record<string, LucideIcon> = { flask: FlaskConical, shield: ShieldCheck, rocket: Rocket, gauge: Gauge, layers: Layers, 'trending-up': TrendingUp }

const ORDER = ['audit.quality', 'audit.security', 'audit.deploy', 'audit.performance', 'audit.architecture', 'audit.scalability']
const rank = (key: string) => (ORDER.includes(key) ? ORDER.indexOf(key) : ORDER.length)

export default function Audit() {
  const { t, locale } = useI18n()
  const { linked, active, choose, loaded } = useActiveRepository()
  const { data: catalog, isLoading } = usePromptCatalog()
  const [sent, setSent] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  // Quality, then Security, then Deploy; any audit the catalog adds later comes after them.
  const audits = (catalog?.prompts ?? [])
    .filter((p) => p.category === 'audit')
    .sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key))

  // "Quem faz?": alone with one agent it runs at once; otherwise the person picks who (components/WhoDoesIt.tsx).
  const who = useWhoDoesIt(active ? { id: active.id, name: active.name } : null)
  const run = useMutation({
    mutationFn: ({ prompt, target }: { prompt: CatalogPrompt; target: WorkTarget }) => runPrompt(prompt, active!.id, locale, target),
    onSuccess: (_, { prompt }) => {
      setSent(prompt.key)
      setProblem(null)
    },
    onError: (error) =>
      setProblem(error instanceof ApiError && error.code && ['remote_off', 'too_many_pending'].includes(error.code) ? t(`work.error.${error.code}`) : t('error.generic')),
  })

  if (loaded && linked.length === 0) {
    return (
      <EmptyState
        title={t('audit.noRepoTitle')}
        body={t('audit.noRepoBody')}
        action={<Link className="btn btn--primary" to="/agents/repositories">{t('repos.link')}</Link>}
      />
    )
  }

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <p className="muted small" style={{ maxWidth: 720, margin: 0 }}>{t('audit.intro')}</p>
        <label className="row row--tight audit__repo">
          <FolderGit2 size={15} aria-hidden />
          <span className="small">{t('audit.on')}</span>
          <select className="select" value={active?.id ?? ''} onChange={(event) => choose(event.target.value)} aria-label={t('run.repository')}>
            {linked.map((repository) => (
              <option key={repository.id} value={repository.id}>
                {repository.name}
                {repository.agentListening ? ` · ${t('repos.listening')}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {problem && <ErrorBanner message={problem} />}
      {who.dialog}
      {isLoading && <Skeleton height={180} />}

      <div className="audit-grid">
        {audits.map((prompt) => {
          const Icon = ICONS[prompt.icon] ?? ShieldCheck
          const isSent = sent === prompt.key
          return (
            <article key={prompt.key} className={`audit-card panel audit-card--${prompt.icon}`}>
              <div className="audit-card__icon" aria-hidden>
                <Icon size={30} />
              </div>
              <h2 className="audit-card__title">{localized(prompt.title, locale)}</h2>
              <p className="small audit-card__what">{localized(prompt.description, locale)}</p>
              <span className="tiny muted">v{prompt.version}{prompt.newerVersion ? ` · ${t('audit.newer', { version: prompt.newerVersion })}` : ''}</span>

              {isSent ? (
                <div className="audit-card__sent small" role="status">
                  <CheckCircle2 size={16} /> {t('audit.sent')} <Link to="/agents/work">{t('run.follow')}</Link>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary audit-card__go"
                  onClick={() => void who.ask((target) => run.mutate({ prompt, target }))}
                  disabled={!active || run.isPending}
                >
                  {run.isPending && run.variables?.prompt.key === prompt.key ? <Loader2 size={16} className="spin" /> : <Icon size={16} />}
                  {t('audit.run', { name: localized(prompt.title, locale) })}
                </button>
              )}

              <Link className="tiny muted audit-card__prompt" to={`/agents/prompts#${prompt.key}`}>
                {prompt.overridden ? t('audit.promptCustom') : t('audit.promptSee')}
              </Link>
            </article>
          )
        })}
      </div>

      <PluginButtons placement="audit" />

      <p className="tiny muted" style={{ margin: 0 }}>{t('audit.disclaimer')}</p>
    </div>
  )
}
